import React, { useState } from "react";
import { IconButton } from "./IconButton";

/**
 * Ronde voortgangsknop voor achtergrond-batches: witte cirkel met een
 * huisstijl-ring die van 0 naar 360 graden meeloopt, "y/x" in het midden en
 * een rode stip bij fouten; klik toont een klein paneel met de details. De
 * markup komt één-op-één uit de upload-tracker (UploadProgressFab), zodat elke
 * batch — uploads linksonder, webinarfacturen rechtsonder — er hetzelfde
 * uitziet.
 */
interface Props {
  done: number;
  total: number;
  /** Fractie (0–1) van het lopende item, voor een vloeiende ring. */
  currentFraction?: number;
  /** Kopregel van het uitklappaneel, bv. "Uploaden naar V012345AB". */
  titel: string;
  /** Onder de kop: wat er nu gebeurt (al opgemaakt, bv. 'Bezig met "x"…'). */
  huidigLabel?: string;
  /** Extra regels in het paneel, onder huidigLabel (bv. één per wachtende batch). */
  regels?: string[];
  /** Foutregel onderin het paneel; leeg = geen fouten en geen stip. */
  foutTekst?: string;
  /** Vervangt "y/x" in het midden van de ring (bv. het aantal wachtende batches). */
  middenTekst?: string;
  /** 0 = onderste knop; elke stap hoger stapelt 52px (44 + 8 ruimte) erboven. */
  stapelIndex?: number;
  positie: "linksonder" | "rechtsonder";
  /** Knop onderin het paneel waarmee de gebruiker de melding wegklikt. */
  sluitKnop?: { label: string; onClick: () => void };
  /** Hoofdactie onderin het paneel, rechts van sluitKnop (bv. "Map kiezen"). */
  actieKnop?: { label: string; onClick: () => void };
}

const STAPEL_STAP = 52;

export function VoortgangsFab({
  done,
  total,
  currentFraction = 0,
  titel,
  huidigLabel,
  regels,
  foutTekst,
  middenTekst,
  stapelIndex = 0,
  positie,
  sluitKnop,
  actieKnop,
}: Props) {
  const [open, setOpen] = useState(false);

  const pct = total > 0 ? Math.min(1, (done + currentFraction) / total) : 0;
  const angle = pct * 360;
  const samenvatting = middenTekst ? titel : `${titel}: ${done}/${total}`;
  // Linksonder stapelt boven de zoekknop (bottom 62) en de notificatietray
  // (bottom 10); rechtsonder is vrij en mag tegen de hoek aan.
  const anker: React.CSSProperties =
    positie === "linksonder"
      ? { left: 10, bottom: 114 + stapelIndex * STAPEL_STAP }
      : { right: 10, bottom: 10 + stapelIndex * STAPEL_STAP };
  const paneelAnker: React.CSSProperties =
    positie === "linksonder" ? { left: 52 } : { right: 52 };

  return (
    <div style={{ position: "fixed", zIndex: 1000, ...anker }}>
      <button
        type="button"
        aria-label={
          middenTekst ? samenvatting : `${titel}: ${done} van ${total}`
        }
        title={samenvatting}
        onClick={() => setOpen((v) => !v)}
        style={{
          position: "relative",
          width: 44,
          height: 44,
          padding: 0,
          borderRadius: 9999,
          border: "none",
          background: `conic-gradient(var(--btn-bg, #a0a08f) ${angle}deg, #e5e7eb ${angle}deg)`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            position: "absolute",
            inset: 4,
            borderRadius: 9999,
            background: "#ffffff",
            border: "1px solid #d1d5db",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            color: "var(--color-text, #111827)",
          }}
        >
          {middenTekst ?? `${done}/${total}`}
        </span>
        {foutTekst && (
          <span
            aria-hidden
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 12,
              height: 12,
              borderRadius: 9999,
              background: "#b91c1c",
              border: "2px solid #fff",
            }}
          />
        )}
      </button>

      {open && (
        <div
          role="status"
          style={{
            position: "absolute",
            bottom: 0,
            width: 280,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            padding: 10,
            fontSize: 12,
            color: "var(--color-text, #111827)",
            ...paneelAnker,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{samenvatting}</div>
          {huidigLabel && (
            <div
              style={{
                color: "#6b7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {huidigLabel}
            </div>
          )}
          {regels?.map((regel, i) => (
            <div
              key={i}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {regel}
            </div>
          ))}
          {foutTekst && (
            <div style={{ marginTop: 6, color: "#b91c1c" }}>{foutTekst}</div>
          )}
          {(sluitKnop || actieKnop) && (
            <div
              style={{
                marginTop: 8,
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
              }}
            >
              {sluitKnop && (
                <IconButton variant="neutral" onClick={sluitKnop.onClick}>
                  {sluitKnop.label}
                </IconButton>
              )}
              {actieKnop && (
                <IconButton variant="brandSolid" onClick={actieKnop.onClick}>
                  {actieKnop.label}
                </IconButton>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
